import { useState, useEffect } from 'react'
import { Link, useNavigate } from 'react-router-dom'
import { motion } from 'framer-motion'
import { User, Phone } from 'lucide-react'
import { useAuth, useClientConfig } from '@workspace/public-utils'
import { Seo } from '../../components/Seo'

const RegisterPage = () => {
  const config = useClientConfig()
  const [formData, setFormData] = useState({
    name: '',
    phone: '',
    agreeToMarketing: false,
    agreeToTerms: false,
    agreeToPrivacy: false
  })
  const [isLoading, setIsLoading] = useState(false)
  const [showTerms, setShowTerms] = useState(false)
  const [showPrivacy, setShowPrivacy] = useState(false)
  const [error, setError] = useState('')
  

  const { register } = useAuth()
  const navigate = useNavigate()

  // Minimal registration: no username/password availability checks

  // Phone number formatting (with debounce to allow deletion)
  useEffect(() => {
    const timer = setTimeout(() => {
      if (formData.phone) {
        const formatPhone = (phone: string) => {
          const digits = phone.replace(/\D/g, '')
          if (digits.length === 0) {
            return ''
          }
          if (digits.length >= 11) {
            return `${digits.slice(0, 3)}-${digits.slice(3, 7)}-${digits.slice(7, 11)}`
          } else if (digits.length >= 7) {
            return `${digits.slice(0, 3)}-${digits.slice(3, 7)}-${digits.slice(7)}`
          } else if (digits.length >= 3) {
            return `${digits.slice(0, 3)}-${digits.slice(3)}`
          }
          return digits
        }

        const formatted = formatPhone(formData.phone)

        // Update form data with formatted phone only if different
        if (formatted !== formData.phone) {
          setFormData(prev => ({ ...prev, phone: formatted }))
        }
      }
    }, 300) // 300ms debounce to allow user to delete characters

    return () => clearTimeout(timer)
  }, [formData.phone])

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, type, checked, value } = e.target
    setFormData({
      ...formData,
      [name]: type === 'checkbox' ? checked : value
    })
  }

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    setIsLoading(true)
    setError('')

    // Validation
    if (!formData.name.trim()) {
      setError('이름을 입력해주세요.')
      setIsLoading(false)
      return
    }

    // No username/password fields in minimal registration

    // Validate phone number format
    const phoneDigits = formData.phone.replace(/\D/g, '')
    if (phoneDigits.length < 10 || phoneDigits.length > 11) {
      setError('휴대폰 번호는 10~11자리 숫자여야 합니다.')
      setIsLoading(false)
      return
    }

    try {
      if (!formData.agreeToTerms || !formData.agreeToPrivacy) {
        setError('이용약관과 개인정보 처리방침에 동의해 주세요.')
        setIsLoading(false)
        return
      }

      await register({
        name: formData.name,
        phone: formData.phone,
        agreeToMarketing: formData.agreeToMarketing
      })
      navigate('/dashboard')
    } catch (err: any) {
      console.error('Registration error:', err)

      let errorMessage = '회원가입에 실패했습니다. 다시 시도해주세요.'

      if (err.response?.data?.detail) {
        const detail = err.response.data.detail
        if (detail.includes('존재하는') && detail.includes('휴대폰 번호')) {
          errorMessage = detail
        } else if (detail.includes('already exists')) {
          errorMessage = '이미 사용 중인 정보입니다.'
        } else if (typeof detail === 'string') {
          errorMessage = detail
        }
      } else if (err.response?.status === 400) {
        errorMessage = '입력 정보를 확인해주세요.'
      }

      setError(errorMessage)
    } finally {
      setIsLoading(false)
    }
  }

  // OAuth removed

  const handleGoBack = () => {
    if (window.history.length > 1) {
      navigate(-1);
    } else {
      navigate('/');
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-happiness-primary/5 via-white to-happiness-secondary/5 py-12 px-4 sm:px-6 lg:px-8">
      <Seo title="회원가입" canonicalPath="/auth/register" noindex />
      <div className="max-w-md mx-auto">
        {/* Navigation */}
        <div className="mb-6 flex justify-between items-center">
          <button
            onClick={handleGoBack}
            className="inline-flex items-center px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 hover:text-happiness-primary transition-all duration-200 focus:outline-none focus:ring-2 focus:ring-happiness-primary focus:ring-offset-2 shadow-sm hover:shadow-md transform hover:scale-[1.02]"
          >
            <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 19l-7-7m0 0l7-7m-7 7h18" />
            </svg>
            뒤로가기
          </button>
          <Link
            to="/"
            className="inline-flex items-center px-4 py-2 text-sm font-medium text-white bg-gradient-to-r from-happiness-primary to-happiness-secondary rounded-lg hover:shadow-lg transition-all duration-200 focus:outline-none focus:ring-2 focus:ring-happiness-primary focus:ring-offset-2 transform hover:scale-[1.02]"
          >
            <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 12l2-2m0 0l7-7 7 7M5 10v10a1 1 0 001 1h3m10-11l2 2m-2-2v10a1 1 0 01-1 1h-3m-6 0a1 1 0 001-1v-4a1 1 0 011-1h2a1 1 0 011 1v4a1 1 0 001 1m-6 0h6" />
            </svg>
            홈으로
          </Link>
        </div>
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
          className="text-center mb-8"
        >
          <Link to="/" className="inline-block">
            <div className="mx-auto w-24 h-24 bg-gradient-to-br from-happiness-primary to-happiness-secondary rounded-full flex items-center justify-center mb-6 shadow-lg hover:shadow-xl transition-shadow">
              <span className="text-3xl text-white font-bold">{config.logoIconText}</span>
            </div>
            <h1 className="gradient-text-happiness text-4xl font-bold mb-2 hover:opacity-80 transition-opacity">
              {config.brandName}
            </h1>
          </Link>
          <p className="text-gray-600">
            {config.registerWelcome}
          </p>
        </motion.div>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, delay: 0.2 }}
          className="bg-white rounded-2xl shadow-xl p-8 border border-gray-100"
        >
          <div className="mb-6">
            <h2 className="text-2xl font-bold text-gray-900 mb-2">회원가입</h2>
            <p className="text-gray-600">새로운 계정을 만들어 복권을 시작하세요</p>
          </div>

          {error && (
            <motion.div
              initial={{ opacity: 0, scale: 0.9 }}
              animate={{ opacity: 1, scale: 1 }}
              className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg mb-6"
            >
              {error}
            </motion.div>
          )}

          <form onSubmit={handleSubmit} className="space-y-6">
            <div>
              <label htmlFor="name" className="block text-sm font-medium text-gray-700 mb-2">
                이름
              </label>
              <div className="relative">
                <User className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 h-5 w-5" />
                <input
                  type="text"
                  id="name"
                  name="name"
                  value={formData.name}
                  onChange={handleInputChange}
                  required
                  className="w-full pl-10 pr-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-happiness-primary focus:border-transparent transition-colors"
                  placeholder="홍길동"
                />
              </div>
            </div>

            <div>
              <label htmlFor="phone" className="block text-sm font-medium text-gray-700 mb-2">
                휴대폰 번호 *
              </label>
              <div className="relative">
                <Phone className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 h-5 w-5" />
                <input
                  type="tel"
                  id="phone"
                  name="phone"
                  value={formData.phone}
                  onChange={handleInputChange}
                  required
                  className="w-full pl-10 pr-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-happiness-primary focus:border-transparent transition-colors"
                  placeholder="010-1234-5678 (자동 형식 변환)"
                />
              </div>
              <p className="mt-1 text-sm text-gray-500">
                휴대폰 번호는 자동으로 000-0000-0000 형식으로 변환됩니다. 초기 비밀번호는 휴대폰 번호의 마지막 4자리입니다.
              </p>
            </div>

            {/* Agreements */}
            <div className="space-y-3">
              <label className="flex items-start gap-3 text-sm text-gray-700">
                <input
                  type="checkbox"
                  name="agreeToTerms"
                  checked={formData.agreeToTerms}
                  onChange={handleInputChange}
                  className="mt-1 h-4 w-4 text-happiness-primary border-gray-300 rounded focus:ring-happiness-primary"
                  required
                />
                <span>
                  <button type="button" onClick={() => setShowTerms(true)} className="text-happiness-primary underline">서비스 이용약관</button>에 동의합니다 (필수)
                </span>
              </label>
              <label className="flex items-start gap-3 text-sm text-gray-700">
                <input
                  type="checkbox"
                  name="agreeToPrivacy"
                  checked={formData.agreeToPrivacy}
                  onChange={handleInputChange}
                  className="mt-1 h-4 w-4 text-happiness-primary border-gray-300 rounded focus:ring-happiness-primary"
                  required
                />
                <span>
                  <button type="button" onClick={() => setShowPrivacy(true)} className="text-happiness-primary underline">개인정보 수집 및 이용</button>에 동의합니다 (필수)
                </span>
              </label>
            </div>

            <button
              type="submit"
              disabled={isLoading}
              className="w-full bg-gradient-to-r from-happiness-primary to-happiness-primary/80 hover:from-happiness-primary/90 hover:to-happiness-primary/70 text-white font-semibold py-3 px-4 rounded-lg transition-all duration-200 disabled:opacity-50 disabled:cursor-not-allowed shadow-lg hover:shadow-xl transform hover:scale-[1.02]"
            >
              {isLoading ? (
                <div className="flex items-center justify-center">
                  <div className="animate-spin rounded-full h-5 w-5 border-2 border-white border-t-transparent mr-2"></div>
                  가입 중...
                </div>
              ) : (
                '회원가입'
              )}
            </button>
          </form>

          {/* OAuth register removed */}

          <div className="text-center mt-6 pt-6 border-t border-gray-200">
            <p className="text-gray-600">
              이미 계정이 있으신가요?{' '}
              <Link
                to="/auth/login"
                className="text-happiness-primary hover:text-happiness-primary/80 font-semibold transition-colors"
              >
                로그인
              </Link>
            </p>
          </div>
        </motion.div>
      </div>
      {/* Terms Modal */}
      {showTerms && (
        <div className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-xl shadow-xl max-w-3xl w-full max-h-[80vh] overflow-hidden">
            <div className="flex items-center justify-between p-4 border-b">
              <h3 className="text-lg font-semibold">서비스 이용약관</h3>
              <button className="text-gray-500 hover:text-gray-700" onClick={() => setShowTerms(false)}>닫기</button>
            </div>
            <div className="h-[70vh]">
              <iframe title="terms" src="/terms" className="w-full h-full"></iframe>
            </div>
          </div>
        </div>
      )}
      {/* Privacy Modal */}
      {showPrivacy && (
        <div className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-xl shadow-xl max-w-3xl w-full max-h-[80vh] overflow-hidden">
            <div className="flex items-center justify-between p-4 border-b">
              <h3 className="text-lg font-semibold">개인정보 처리방침</h3>
              <button className="text-gray-500 hover:text-gray-700" onClick={() => setShowPrivacy(false)}>닫기</button>
            </div>
            <div className="h-[70vh]">
              <iframe title="privacy" src="/privacy" className="w-full h-full"></iframe>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}

export default RegisterPage
